<template>
  <section class="anamnesis-observations">

    <div class="d-flex justify-content-between flex-wrap align-items-center">
      <h6 class="text-evollu-gray-light is-bold py-3">
        {{$t('clinical.people.anamnesis.outcome')}}
      </h6>
      <i
        v-if="collapsable"
        class="clickable p-3"
        :class="{'fa fa-chevron-up': !isCollapsed, 'fa fa-chevron-down': isCollapsed}"
        @click="isCollapsed = !isCollapsed"
      >
      </i>
    </div>

    <div>
      <form class="evollu-form">

        <slot name="beforeForm"></slot>

        <!-- Outcome -->
        <div class="row align-items-center">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.appointments.outcome')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <multiselect
              :value="value && value.status_id ? appointmentStatusesOptions.find(status => status.id === value.status_id) : null"
              :options="appointmentStatusesOptions"
              @input="val => updateValue('status_id', val ? val.id : null)"
              track-by="id"
              :customLabel="option => `${$t(`clinical.people.anamnesis.outcome_translations.${(option.name).toLowerCase()}`)}`"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('clinical.appointments.outcome')"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              :close-on-select="true"
              open-direction="bottom"
              :disabled="!editing"
              >
              <template slot="option" slot-scope="props">
                <span>{{ `${$t(`clinical.people.anamnesis.outcome_translations.${(props.option.name).toLowerCase()}`)}` }}</span>
              </template>
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              </multiselect>
            </div>

          <div class="text-danger col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('status_id')">
            <small>
              {{$t(formErrors.get('status_id'))}}
            </small>
          </div>
        </div>

        <!-- Outcome Reason -->
        <div class="row align-items-center" v-if="appointmentStatusesReasonsOptions.length">
          <label class="is-bold col-12 col-sm-5 col-md-4 mb-0 text-sm-right">
            {{$t('clinical.people.anamnesis.outcome_reason')}}
          </label>

          <div class="col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6">
            <multiselect
              :value="value && value.status_reason_id ? appointmentStatusesReasonsOptions.find(status => status.id === value.status_reason_id) : null"
              :options="appointmentStatusesReasonsOptions"
              @input="val => updateValue('status_reason_id', val ? val.id : null)"
              track-by="id"
              :customLabel="option => `${$t(`clinical.people.anamnesis.outcome_translations.${(option.name).toLowerCase()}`)}`"
              select-label=""
              selected-label=""
              deselect-label=""
              :placeholder="$t('clinical.people.anamnesis.outcome_reason')"
              :allow-empty="true"
              :multiple="false"
              :searchable="true"
              :close-on-select="true"
              open-direction="bottom"
              :disabled="!editing"
              >
              <template slot="option" slot-scope="props">
                <span>{{ `${$t(`clinical.people.anamnesis.outcome_translations.${(props.option.name).toLowerCase()}`)}` }}</span>
              </template>
              <span slot="noResult">{{$t('patients.noResults')}}</span>
              </multiselect>
            </div>

          <div class="text-danger col-12 col-sm-7 col-md-8 col-lg-7 col-xl-6 offset-sm-5 offset-md-4" v-if="formErrors.has('status_reason_id')">
            <small>
              {{$t(formErrors.get('status_reason_id'))}}
            </small>
          </div>
        </div>

      </form>
    </div>
  </section>
</template>

<script>
import Errors from '@/utils/StandardErrors'
import Multiselect from 'vue-multiselect'
import { AppointmentService } from '@/api/AuthClinicalService'
const STATUSES = {
  NO_APPOINTMENT: 1,
  NO_TEST: 2,
  NO_LOSS: 3,
  NO_SELL: 4,
  SELL: 5
}
export default {
  components: {
    Multiselect
  },
  props: {
    value: {
      type: Object,
      required: false
    },
    formErrors: {
      type: Object,
      required: false,
      default: () => {
        return new Errors()
      }
    },
    editing: {
      type: Boolean,
      default: true
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    },
    anamnese: {
      type: Object,
      required: true
    },
    service: {
      type: Object,
      required: true
    }
  },
  data: function () {
    return {
      isCollapsed: this.collapsable && this.collapsed,
      availableAppointmentStatuses: []
    }
  },
  created () {
    this.loadAppointmentStatuses()
  },
  methods: {
    loadAppointmentStatuses: function () {
      AppointmentService.getStatuses(this.service.url)
        .then(statuses => {
          this.availableAppointmentStatuses = statuses
        }).catch(errors => {
          console.log(errors)
        })
    },
    updateValue: function (field, value) {
      this.$emit('input', { ...this.value, [field]: value })
      this.$emit('clearErrors', field)
    },
    getDefaultValue: function () {
      return {
        status_id: null,
        status_reason_id: null
      }
    }
  },
  computed: {
    appointmentStatusesReasonsOptions: function () {
      if (!this.value || !this.value.status_id) {
        return []
      }
      return this.availableAppointmentStatuses.filter(status => status.parent_id === this.value.status_id)
    },
    appointmentStatusesOptions: function () {
      let statuses = this.availableAppointmentStatuses.filter(status => !status.parent_id)

      statuses = statuses.filter(status => this.anamnese.with_sale ? status.id === STATUSES.SELL : status.id !== STATUSES.SELL)

      if (this.anamnese.with_tonal_audiometry && this.anamnese.tonal_audiometry) {
        statuses = statuses.filter(status => status.id !== STATUSES.NO_APPOINTMENT && status.id !== STATUSES.NO_TEST)
      }

      return statuses
    },
  },
  watch: {
    appointmentStatusesOptions: function (options) {
      if (this.value.status_id && options.length && !options.some(status => status.id === this.value.status_id)) {
        this.updateValue('status_id', null)
      }
    },
    appointmentStatusesReasonsOptions: function (options) {
      if (this.value.status_reason_id && options.length && !options.some(status => status.id === this.value.status_reason_id)) {
        this.updateValue('status_reason_id', null)
      }
    },
    'value.status_id': function (statusId) {
      if (this.value.status_reason_id) {
        this.updateValue('status_reason_id', null)
      }
    },
    collapsed: function () {
      this.isCollapsed = this.collapsable && this.collapsed
    },
    editing: {
      handler: function (editing) {
        if (editing) {
          this.$emit('input', { ...this.getDefaultValue(), ...this.value })
        }
      },
      immediate: true
    }
  }
}
</script>
